import { Loader } from "@/components/Loader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrowRight } from "lucide-react";
import { KeyboardEvent } from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import {
  PipelineHealthStageChart,
  PipelineRiskBubbleChart,
} from "./PipelineAnalysisCharts";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import { useStateOfSalesV2Forecast } from "./useForecastData";
import { useStateOfSalesV2PipelineData } from "./usePipelineData";
import {
  buildPipelineConcentrationSummary,
  pipelineRiskSeverityColors,
  selectPipelineRiskBubbleDeals,
  type PipelineRiskBubbleDeal,
  type PipelineRiskSeverityRow,
} from "./pipelineAnalysis";
import {
  stateOfSalesV2L1Layout,
  stateOfSalesV2LegendClassName,
  stateOfSalesV2LegendSwatchClassName,
} from "./visualTokens";

type CompanyCurrency = ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
type PipelineData = ReturnType<typeof useStateOfSalesV2PipelineData>;

function handleCardKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  open: () => void,
) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  open();
}

function formatShare(value: number) {
  return new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
    style: "percent",
  }).format(value);
}

const pipelineRiskLegendLabels = {
  high: "High",
  medium: "Medium",
  low: "Low",
} as const;

function PipelineRiskCompositionBar({
  companyCurrency,
  severity,
  totalValue,
}: {
  companyCurrency: CompanyCurrency;
  severity: PipelineRiskSeverityRow[];
  totalValue: number;
}) {
  const orderedRows = (["high", "medium", "low"] as const)
    .map(key => severity.find(row => row.key === key))
    .filter((row): row is PipelineRiskSeverityRow => Boolean(row));

  return (
    <div className={cn(
      "space-y-2 rounded-[8px] border border-gray-200 bg-gray-50/60 px-4 py-3",
      stateOfSalesV2L1Layout.compositionBand,
    )}>
      <p className="text-[11px] font-medium text-gray-500">
        Open deal value by Pipeline Risk
      </p>
      {totalValue > 0 ? (
        <>
          <div className="flex h-3 overflow-hidden rounded-full bg-white ring-1 ring-gray-200">
            {orderedRows.map((row) => {
              const share = row.rawValue / totalValue;
              if (share <= 0) return null;
              return (
                <Tooltip key={row.key}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${row.label}: ${formatShare(share)} of open deal value`}
                      className="h-full min-w-px transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                      style={{
                        backgroundColor: pipelineRiskSeverityColors[row.key],
                        width: `${share * 100}%`,
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="text-xs leading-5"
                  >
                    <p className="font-medium">{row.label}</p>
                    <p>
                      {formatCompactIsoCurrency(row.rawValue, companyCurrency)}
                      {" · "}
                      {formatShare(share)}
                      {" · "}
                      {row.count} {row.count === 1 ? "deal" : "deals"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          <div
            aria-label="Pipeline Risk composition legend"
            className={stateOfSalesV2LegendClassName}
          >
            {orderedRows.map((row) => (
              <span key={row.key} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={stateOfSalesV2LegendSwatchClassName}
                  style={{ backgroundColor: pipelineRiskSeverityColors[row.key] }}
                />
                {pipelineRiskLegendLabels[row.key]} {formatShare(row.rawValue / totalValue)}
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="text-[11px] text-gray-500">No open deal value in this selection.</p>
      )}
    </div>
  );
}

export function PipelineRiskOverviewCard({
  companyCurrency,
  error,
  href,
  isLoading,
  open,
  openDeal,
  risk,
  selectedPeriodOpenDeals,
  urgencyDeals,
}: {
  companyCurrency: CompanyCurrency;
  error: unknown;
  href: string;
  isLoading: boolean;
  open: () => void;
  openDeal: (deal: PipelineRiskBubbleDeal) => void;
  risk: PipelineData["risk"];
  selectedPeriodOpenDeals: PipelineData["selectedPeriodOpenDeals"];
  urgencyDeals: PipelineData["urgencyDeals"];
}) {
  const highRisk = risk?.severity.find(row => row.key === "high");
  const concentration = buildPipelineConcentrationSummary(
    selectedPeriodOpenDeals,
  );
  const bubbleDeals = risk
    ? selectPipelineRiskBubbleDeals(
      risk.deals,
      urgencyDeals,
    )
    : [];
  const highRiskDealValue = highRisk?.rawValue ?? 0;
  const hasRiskPopulation = Boolean(risk?.deals.length);
  const priorityDealCopy = `${bubbleDeals.length} priority risk ${
    bubbleDeals.length === 1 ? "deal" : "deals"
  }`;
  const concentrationLabel = concentration.state === "unavailable"
    ? "—"
    : `${concentration.dealCount} ${
      concentration.dealCount === 1 ? "deal" : "deals"
    }`;
  const concentrationSupport = concentration.state === "unavailable"
    ? "No open pipeline in this period"
    : `${formatShare(concentration.representedShare)} of open pipeline`;

  return (
    <Card
      className="h-full cursor-pointer border-[#9F86C0]/40 bg-[#9F86C0]/[0.025] transition-colors hover:border-[#9F86C0]/70 hover:bg-[#9F86C0]/[0.05]"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => handleCardKeyDown(event, open)}
      data-href={href}
    >
      <CardHeader className="p-5 pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-base">Pipeline Risk</CardTitle>
          <Link
            href={href}
            className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-[#5E548E] hover:text-[#231942]"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            Open Pipeline Risk
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-0">
        {isLoading ? (
          <div className="flex min-h-[364px] items-center justify-center" aria-label="Loading Pipeline Risk">
            <Loader size={32} parentClassName="" />
          </div>
        ) : error ? (
          <div className="flex min-h-[364px] items-center justify-center rounded-[8px] border border-dashed border-gray-200 p-5 text-center text-xs text-gray-500">
            Pipeline Risk data could not be loaded. Open Pipeline Risk to try again.
          </div>
        ) : (
          <>
        <div className={cn(
          "overflow-hidden rounded-[8px] border border-gray-200 bg-gray-50/60 p-4",
          stateOfSalesV2L1Layout.positionBand,
        )}>
          <p className="text-[11px] font-medium text-gray-500">
            Pipeline Risk position
          </p>
          <div className="mt-2 grid grid-cols-2">
            <div className="min-w-0 border-r border-gray-200 pr-3">
              <p className="text-[10px] leading-4 text-gray-500">
                High-risk deal value
              </p>
              <p className="mt-1 text-sm font-medium tabular-nums text-[#231942]">
                {formatCompactIsoCurrency(highRiskDealValue, companyCurrency)}
              </p>
            </div>
            <div className="min-w-0 pl-3">
              <p className="text-[10px] leading-4 text-gray-500">
                Pipeline concentration
              </p>
              <p className="mt-1 text-sm font-medium tabular-nums text-[#231942]">
                {concentrationLabel}
              </p>
              <p className="mt-1 text-[10px] leading-4 text-gray-500">
                {concentrationSupport}
                {concentration.state !== "unavailable" && (
                  <>
                    {" · "}
                    <span className={
                      concentration.state === "high"
                        ? "text-[#9F3A4A]"
                        : "text-[#1D819A]"
                    }>
                      {concentration.state === "high"
                        ? "High concentration"
                        : "Balanced"}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
        <PipelineRiskCompositionBar
          companyCurrency={companyCurrency}
          severity={risk?.severity ?? []}
          totalValue={risk?.exposedOpenDealValue ?? 0}
        />
        {hasRiskPopulation ? (
          <div className="space-y-2">
            <div className={stateOfSalesV2L1Layout.visualHeader}>
              <p className="text-xs font-medium text-gray-700">{priorityDealCopy}</p>
              {bubbleDeals.some(deal => deal.urgency_bucket === "high") && (
                <p className="inline-flex items-center gap-1.5 text-[10px] text-gray-500">
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 rounded-full border-2 border-[#E0B1CB]"
                  />
                  High urgency
                </p>
              )}
            </div>
            <div>
              <PipelineRiskBubbleChart
                companyCurrency={companyCurrency}
                data={bubbleDeals}
                onSelect={openDeal}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className={stateOfSalesV2L1Layout.visualHeader}>
              <p className="text-xs font-medium text-gray-700">Priority risk deals</p>
            </div>
            <div className="flex min-h-64 items-center justify-center rounded-[8px] border border-dashed border-gray-200 p-5 text-center text-xs text-gray-500">
              No open deals with Pipeline Risk indicators are expected to close in this period.
            </div>
          </div>
        )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function PipelineHealthOverviewCard({
  error,
  health,
  historical,
  href,
  isLoading,
  open,
}: {
  error: unknown;
  health: PipelineData["health"];
  historical: boolean;
  href: string;
  isLoading: boolean;
  open: () => void;
}) {
  const weakStages = health?.stageRows.filter(row => row.coveragePercentage < 100) ?? [];

  return (
    <Card
      className="h-full cursor-pointer border-[#1D819A]/35 bg-[#1D819A]/[0.02] transition-colors hover:border-[#1D819A]/65 hover:bg-[#1D819A]/[0.04]"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => handleCardKeyDown(event, open)}
      data-href={href}
    >
      <CardHeader className="space-y-2 p-5 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] text-gray-500">Time-adjusted coverage</p>
            <CardTitle className="mt-1 text-xl tabular-nums">
              {health ? `${(health.coveragePercentage / 100).toFixed(1)}x` : "—"}
            </CardTitle>
          </div>
          <Badge variant="outline" className="font-normal text-gray-500">
            30-day change unavailable
          </Badge>
        </div>
        <p className="text-xs text-gray-500">
          Existing risk-, stage- and time-adjusted coverage against the selected-period target.
        </p>
      </CardHeader>
      <CardContent className="p-5 pt-2">
        {isLoading ? (
          <div className="flex min-h-48 items-center justify-center" aria-label="Loading Pipeline Health">
            <Loader size={32} parentClassName="" />
          </div>
        ) : error ? (
          <div className="flex min-h-48 items-center justify-center rounded-[8px] border border-dashed border-gray-200 p-5 text-center text-xs text-gray-500">
            Pipeline Health data could not be loaded. Open Pipeline Health to try again.
          </div>
        ) : historical ? (
          <div className="flex min-h-48 items-center justify-center rounded-[8px] border border-dashed border-gray-200 p-5 text-center text-xs text-gray-500">
            Historical Pipeline Health requires a point-in-time open-pipeline snapshot for the selected period.
          </div>
        ) : health ? (
          <>
            <p className="text-xs font-medium text-gray-700">Coverage by adjusted probability band</p>
            <PipelineHealthStageChart compact data={health.stageRows} />
            <p className="text-xs leading-5 text-gray-600">
              {weakStages.length
                ? `${health.weakestStage?.label ?? "The weakest stage band"} has the largest supported coverage shortage.`
                : "Coverage meets the existing requirement across all adjusted probability bands."}
            </p>
          </>
        ) : (
          <div className="flex min-h-48 items-center justify-center rounded-[8px] border border-dashed border-gray-200 p-5 text-center text-xs text-gray-500">
            Pipeline Health cannot be calculated because the selected-period target is missing.
          </div>
        )}
        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[#5E548E]">
          Open Pipeline Health
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </CardContent>
    </Card>
  );
}
